import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, Paper, Stack, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { ReplayEngine, type PlayerState, type PlaybackSpeed, type ReplayTimeline } from '@replayops/replay-core';
import { api } from '../api/client.js';
import { ReplayScrubber } from '../components/ReplayScrubber.js';
import { useT } from '../i18n/useT.js';

const STAGE_W = 1440;
const STAGE_H = 900;
const fmt = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

interface PublicReplay {
  sessionId: string;
  events: unknown[];
  meta: { durationMs: number; startedAt: string; browser?: string; os?: string; deviceType?: string; entryUrl: string };
}

/**
 * Standalone, unauthenticated replay viewer reached via a public share link
 * (/share/:token). No app shell, no login — just the recording + controls.
 */
export function PublicReplayPage() {
  const c = useT().replay.publicReplay;
  const { token } = useParams();
  const stageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<ReplayEngine | null>(null);
  const [scale, setScale] = useState(1);
  const [state, setState] = useState<PlayerState>({ playing: false, currentTimeMs: 0, durationMs: 0, speed: 1 });
  const [timeline, setTimeline] = useState<ReplayTimeline | null>(null);

  const q = useQuery({
    enabled: !!token,
    retry: false,
    queryKey: ['public-replay', token],
    queryFn: async () => (await api.get(`/v1/public/replays/${token}`)).data as PublicReplay,
  });

  const applyScale = useCallback(() => {
    const el = containerRef.current;
    if (el) setScale(Math.min(1, el.clientWidth / STAGE_W));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    applyScale();
    const ro = new ResizeObserver(applyScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, [applyScale, q.data]);

  useEffect(() => {
    if (!q.data?.events || !stageRef.current || q.data.events.length < 2) return;
    stageRef.current.innerHTML = '';
    const engine = new ReplayEngine(q.data.events as never, stageRef.current);
    engineRef.current = engine;
    setTimeline(engine.getTimeline());
    const off = engine.on(setState);
    engine.play();
    return () => {
      off();
      engine.destroy();
      engineRef.current = null;
    };
  }, [q.data]);

  const engine = () => engineRef.current;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Minimal branded top bar */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: 'primary.main', display: 'grid', placeItems: 'center' }}>
          <PlayArrowIcon sx={{ fontSize: 15, color: '#04231f' }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={800}>
          Replay<Box component="span" sx={{ color: 'primary.main' }}>Ops</Box>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>{c.shared}</Typography>
      </Stack>

      <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {q.isLoading && <Typography color="text.secondary">{c.loading}</Typography>}
        {q.isError && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>{c.unavailable}</Typography>
            <Typography variant="body2" color="text.secondary">
              {c.unavailableBody}
            </Typography>
          </Paper>
        )}

        {q.data && (
          <>
            {q.data.meta && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formatDateTime(q.data.meta.startedAt)} · {q.data.meta.browser ?? c.browser} · {q.data.meta.deviceType ?? c.device} · {fmt(q.data.meta.durationMs)}
              </Typography>
            )}
            <Paper sx={{ p: 0, overflow: 'hidden' }}>
              <Box ref={containerRef} sx={{ position: 'relative', width: '100%', height: STAGE_H * scale, bgcolor: '#fff' }}>
                <Box
                  ref={stageRef}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: STAGE_W,
                    height: STAGE_H,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    '& .replayer-wrapper': { position: 'absolute', top: 0, left: 0, transform: 'none' },
                    '& iframe': { border: 'none' },
                  }}
                />
              </Box>
            </Paper>

            <Paper sx={{ p: 1, mt: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton onClick={() => engine()?.toggle()} color="primary">
                  {state.playing ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 90 }}>
                  {fmt(state.currentTimeMs)} / {fmt(state.durationMs)}
                </Typography>
                <ReplayScrubber
                  durationMs={state.durationMs}
                  currentTimeMs={state.currentTimeMs}
                  markers={timeline?.markers ?? []}
                  onSeek={(ms) => engine()?.seek(ms)}
                />
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={state.speed}
                  onChange={(_, v) => v && engine()?.setSpeed(v as PlaybackSpeed)}
                >
                  {[1, 2, 4, 8].map((s) => (
                    <ToggleButton key={s} value={s}>{s}x</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
}
